import React, { useState, useEffect } from 'react';
import { Box, Typography, Slider, Paper, alpha } from '@mui/material';
import { LeaseTier } from '../../../types';
import { colors } from '../../../styles/colors';

export interface ParameterSliderProps {
  /** Label des Parameters */
  label: string;
  /** Minimaler Wert */
  min: number;
  /** Maximaler Wert */
  max: number;
  /** Schrittweite */
  step: number;
  /** Einheit (z.B. "%") */
  unit: string;
  /** Icon-Element (z.B. <MapIcon />) */
  icon: React.ReactNode;
  /** Farbe für Slider und Akzente (Hex-Wert) */
  color: string;
  /** Deaktiviert den Slider */
  disabled?: boolean;

  // Simple Mode (für einzelnen Wert)
  /** Aktueller Wert (nur im Simple-Mode) */
  value?: number;
  /** Callback bei Wertänderung – feuert nur bei Loslassen (mouseup/touchend) */
  onChange?: (value: number) => void;

  // Tiers Mode (für Pachtkostenstaffeln)
  /** Liste der Pachtkostenstaffeln (nur im Tiers-Mode) */
  leaseTiers?: LeaseTier[];
  /** Projektlaufzeit in Jahren (nur im Tiers-Mode) */
  projektLaufzeit?: number;
  /** Callback wenn der Prozentsatz einer Staffel geändert wird – feuert nur bei Loslassen */
  onTierPercentageChange?: (tierId: string, percentage: number) => void;
}

/**
 * Berechnet das letzte Jahr für eine Staffel
 */
const calculateLastYear = (
  tier: LeaseTier,
  index: number,
  allTiers: LeaseTier[],
  projektLaufzeit: number
): number => {
  if (index < allTiers.length - 1) {
    return allTiers[index + 1].first_year - 1;
  }
  return projektLaufzeit;
};

/**
 * Formatiert einen Zahlenwert im deutschen Format
 */
const formatValue = (value: number, step: number): string => {
  return value.toLocaleString('de-DE', {
    minimumFractionDigits: step < 1 ? 1 : 0,
    maximumFractionDigits: step < 1 ? 2 : 0,
  });
};

/**
 * Erzeugt Slider-Styles für eine gegebene Farbe
 */
const getSliderStyles = (color: string) => ({
  height: 6,
  '& .MuiSlider-track': {
    border: 'none',
    background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
    boxShadow: `0 2px 6px ${alpha(color, 0.25)}`,
  },
  '& .MuiSlider-rail': {
    backgroundColor: alpha(color, 0.15),
  },
  '& .MuiSlider-thumb': {
    width: 18,
    height: 18,
    backgroundColor: '#fff',
    border: `2px solid ${color}`,
    boxShadow: `0 2px 6px ${alpha(color, 0.25)}`,
    transition: 'all 0.15s ease',
    '&:hover, &.Mui-focusVisible': {
      boxShadow: `0 4px 12px ${alpha(color, 0.35)}`,
    },
    '&.Mui-active': {
      boxShadow: `0 4px 14px ${alpha(color, 0.45)}`,
    },
  },
  '& .MuiSlider-valueLabel': {
    backgroundColor: color,
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontWeight: 600,
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
  },
});

/**
 * ParameterSlider - Universeller Slider für Einzel- und Staffelwerte
 * 
 * Simple Mode: Zeigt einen einzelnen Wert mit Slider
 * Tiers Mode: Zeigt bei mehreren Staffeln einen Min-Max Bereich im Hauptwert.
 *             Bei Hover expandiert die Komponente und zeigt alle Staffeln.
 */
const ParameterSlider: React.FC<ParameterSliderProps> = ({
  label,
  min,
  max,
  step,
  unit,
  icon,
  color,
  disabled = false,
  value,
  onChange,
  leaseTiers,
  projektLaufzeit,
  onTierPercentageChange,
}) => {
  // Modus automatisch erkennen
  const isSimpleMode = value !== undefined && onChange !== undefined;
  const isTiersMode = leaseTiers !== undefined && leaseTiers.length > 0;

  // Simple Mode: Einzelner Wert
  const [localValue, setLocalValue] = useState(value ?? 0);

  // Tiers Mode: Mehrere Staffeln
  const [localValues, setLocalValues] = useState<Record<string, number>>({});

  // Sync mit externen Werten (Simple Mode)
  useEffect(() => {
    if (isSimpleMode && value !== undefined) {
      setLocalValue(value);
    }
  }, [value, isSimpleMode]);

  // Sync mit externen Werten (Tiers Mode)
  useEffect(() => {
    if (isTiersMode && leaseTiers) {
      const values: Record<string, number> = {};
      leaseTiers.forEach(tier => {
        values[tier.id] = tier.percentage;
      });
      setLocalValues(values);
    }
  }, [leaseTiers, isTiersMode]);

  const handleSliderChangeSimple = (_: Event, newValue: number | number[]) => {
    setLocalValue(newValue as number);
  };

  const handleSliderCommitSimple = (_: React.SyntheticEvent | Event, newValue: number | number[]) => {
    const val = newValue as number;
    setLocalValue(val);
    onChange?.(val);
  };

  const handleSliderChangeTiers = (tierId: string) => (_: Event, newValue: number | number[]) => {
    setLocalValues(prev => ({ ...prev, [tierId]: newValue as number }));
  };

  const handleSliderCommitTiers = (tierId: string) => (_: React.SyntheticEvent | Event, newValue: number | number[]) => {
    const val = newValue as number;
    setLocalValues(prev => ({ ...prev, [tierId]: val }));
    onTierPercentageChange?.(tierId, val);
  };

  // Werte für Anzeige berechnen
  let displayValue: string;
  let hasMultipleTiers = false;
  let firstTier: LeaseTier | undefined;

  if (isSimpleMode) {
    // Simple Mode
    displayValue = formatValue(localValue, step);
  } else if (isTiersMode && leaseTiers) {
    // Tiers Mode
    const percentages = leaseTiers.map(t => localValues[t.id] ?? t.percentage);
    const minPercentage = Math.min(...percentages);
    const maxPercentage = Math.max(...percentages);
    hasMultipleTiers = leaseTiers.length > 1;
    firstTier = leaseTiers[0];
    const firstTierValue = localValues[firstTier?.id] ?? firstTier?.percentage ?? 0;

    displayValue = hasMultipleTiers
      ? `${formatValue(minPercentage, step)} - ${formatValue(maxPercentage, step)}`
      : formatValue(firstTierValue, step);
  } else {
    // Fallback
    displayValue = '0';
  }

  // Slider-Styles einmalig berechnen
  const sliderStyles = getSliderStyles(color);

  const renderTierRow = (tier: LeaseTier, index: number, isLast: boolean) => {
    if (!leaseTiers || !projektLaufzeit) return null;

    const lastYear = calculateLastYear(tier, index, leaseTiers, projektLaufzeit);
    const tierValue = localValues[tier.id] ?? tier.percentage;

    return (
      <Box
        key={tier.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {/* Zeitspanne Label */}
        {hasMultipleTiers && (
          <Typography
            variant="caption"
            sx={{
              color: alpha(color, 0.8),
              fontWeight: 600,
              fontSize: '0.7rem',
              whiteSpace: 'nowrap',
              minWidth: 70,
            }}
          >
            Jahr {tier.first_year}-{lastYear}
          </Typography>
        )}

        {/* Slider */}
        <Box sx={{ flex: 1 }}>
          <Slider
            value={tierValue}
            onChange={handleSliderChangeTiers(tier.id)}
            onChangeCommitted={handleSliderCommitTiers(tier.id)}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${formatValue(value, step)} ${unit}`}
            sx={sliderStyles}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'visible',
          borderRadius: '16px',
          background: colors.background.paper,
          boxShadow: `0 4px 12px ${colors.shadow.light}`,
          transition: 'box-shadow 0.2s ease-in-out',
          zIndex: 1,
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          '&:hover': {
            boxShadow: `0 4px 16px ${colors.shadow.medium}`,
            zIndex: 10,
            '& .hover-content': {
              maxHeight: '400px',
              opacity: 1,
            },
          },
        }}
      >
        <Box sx={{ pt: 2.5, px: 2.5, pb: 0, position: 'relative', zIndex: 1 }}>
          {/* Icon links, Label + Wert rechts */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            {/* Icon */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${alpha(color, 0.05)} 0%, ${alpha(color, 0.02)} 100%)`,
                color: color,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>

            {/* Content: Label + Wert */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Label */}
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  display: 'block',
                  mb: 0.25,
                }}
              >
                {label}
              </Typography>

              {/* Wert: Min-Max bei mehreren Staffeln, sonst einzelner Wert */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: '1.75rem',
                    fontWeight: 500,
                    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                    color: 'text.primary',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.1,
                  }}
                >
                  {displayValue}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: 'text.primary',
                  }}
                >
                  {unit}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Slider */}
          <Box sx={{ px: 0.5 }}>
            {isSimpleMode ? (
              // Simple Mode: Ein einfacher Slider
              (<Slider
                value={localValue}
                onChange={handleSliderChangeSimple}
                onChangeCommitted={handleSliderCommitSimple}
                min={min}
                max={max}
                step={step}
                disabled={disabled}
                sx={sliderStyles}
              />)
            ) : (
              // Tiers Mode: Erste Staffel
              (firstTier && renderTierRow(firstTier, 0, true))
            )}
          </Box>
        </Box>

        {/* Hover-Content: Weitere Staffeln (nur Tiers Mode, expandiert bei Hover) */}
        {isTiersMode && hasMultipleTiers && leaseTiers && (
          <Box
            className="hover-content"
            sx={{
              maxHeight: 0,
              overflow: 'hidden',
              opacity: 0,
              transition: 'all 0.3s ease-in-out',
              px: 3,
            }}
          >
            {leaseTiers.slice(1).map((tier, idx) =>
              renderTierRow(tier, idx + 1, idx === leaseTiers.length - 2)
            )}
          </Box>
        )}

        {/* Min/Max Labels - immer am unteren Rand */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2.5, pb: 1.5, pt: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
            {min.toLocaleString('de-DE')} {unit}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
            {max.toLocaleString('de-DE')} {unit}
          </Typography>
        </Box>

      </Paper>
    </Box>
  );
};

export default ParameterSlider;
